import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import PitchesPerInningChart from './PitchesPerInningChart';
import StrikeZoneChart from './StrikeZoneChart';
import SequenceChart from './SequenceChart';

const ExportLayout = React.forwardRef(({ snapshot, history, pitchesPerInningData }, ref) => {
    if (!snapshot) return null;

    const {
        date,
        pitcherName,
        opponent,
        pitches,
        strikes,
        balls,
        outs,
        innings,
        runsAllowed,
        strikeouts,
        walks,
        hits,
        stats,
        strikeRate,
        outRate,
        runsPerInning,
    } = snapshot;

    const renderStat = (label, value) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px solid #eee' }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight="bold">{value}</Typography>
        </Box>
    );

    return (
        <Box
            ref={ref}
            sx={{
                width: '1200px', // Fixed width for consistent export
                bgcolor: '#ffffff',
                p: 4,
                position: 'absolute',
                top: 0,
                left: -9999, // Hide off-screen
            }}
        >
            <Typography variant="h4" gutterBottom>
                Pitch Counter Report
            </Typography>
            <Typography variant="subtitle1" gutterBottom color="text.secondary">
                {date} | {pitcherName || 'Unknown Pitcher'} vs {opponent || 'Unknown Opponent'}
            </Typography>

            <Grid container spacing={4} sx={{ mt: 2 }}>
                {/* Left Column: Stats */}
                <Grid item xs={4}>
                    <Paper elevation={0} variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Game Stats</Typography>

                        <Box sx={{ mb: 3 }}>
                            {renderStat('Pitches', pitches)}
                            {renderStat('Strikes', strikes)}
                            {renderStat('Balls', balls)}
                            {renderStat('Outs', outs)}
                            {renderStat('Innings', innings)}
                            {renderStat('Runs Allowed', runsAllowed)}
                        </Box>

                        <Typography variant="h6" gutterBottom>Advanced Stats</Typography>
                        <Box>
                            {renderStat('Strikeouts (K)', strikeouts)}
                            {renderStat('Walks (BB)', walks)}
                            {renderStat('Hits (H)', hits)}
                            {renderStat('K/9', stats.k9)}
                            {renderStat('BB/9', stats.bb9)}
                            {renderStat('K/BB', stats.kbb)}
                            {renderStat('WHIP', stats.whip)}
                            {renderStat('P/IP', stats.pip)}
                            {renderStat('Strike Rate', `${strikeRate}%`)}
                            {renderStat('Outs Rate', `${outRate} P/Out`)}
                            {renderStat('Runs/Inning', runsPerInning)}
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column: Visualizations */}
                <Grid item xs={8}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" align="center">Sequence (Pitch-by-Pitch)</Typography>
                                <SequenceChart history={history} current={snapshot} />
                            </Paper>
                        </Grid>
                        <Grid item xs={6}>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" align="center">Pitches per Inning</Typography>
                                <PitchesPerInningChart data={pitchesPerInningData} />
                            </Paper>
                        </Grid>
                        <Grid item xs={6}>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" align="center">Ratio</Typography>
                                <StrikeZoneChart strikes={strikes} balls={balls} hits={hits} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
});

export default ExportLayout;
